import React, { useMemo } from "react";
import { CalendarClock, Zap } from "lucide-react";
import type { PusherSlot, PusherState, SeoMarket } from "./types";
import { DAY_LABEL, MARKET_TZ, MODE_LABEL, fmtDay, localParts } from "./engineFormat";

// Day axis 07:00-19:00 local time; the publishing window and each jittered slot are drawn on it.
const AXIS_START = 7 * 60;
const AXIS_END = 19 * 60;
const pct = (minutes: number) =>
  `${Math.max(0, Math.min(100, ((minutes - AXIS_START) / (AXIS_END - AXIS_START)) * 100))}%`;

const SLOT_STYLE: Record<PusherSlot["status"], string> = {
  assigned: "bg-[#0F3F34] border-[#0F3F34]",
  open: "bg-white border-slate-400",
  fired: "bg-slate-400 border-slate-400",
  skipped: "bg-amber-300 border-amber-400",
  missed: "bg-amber-500 border-amber-500",
};

const SLOT_TEXT: Record<PusherSlot["status"], string> = {
  assigned: "Planlagt",
  open: "Ledig – ingen godkendt artikel",
  fired: "Udgivet",
  skipped: "Sprunget over",
  missed: "Ikke nået",
};

interface Props {
  market: SeoMarket;
  state: PusherState;
}

export default function PusherTimeline({ market, state }: Props) {
  const tz = state.settings.timezone || MARKET_TZ[market];
  const now = Date.now();
  const rows = useMemo(() => {
    const byDay = new Map<string, PusherSlot[]>();
    state.slots.forEach((s) => byDay.set(s.date, [...(byDay.get(s.date) || []), s]));
    return Array.from(byDay.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .filter(([day]) => new Date(`${day}T23:59:59Z`).getTime() >= now - 3 * 86400000);
  }, [state.slots, now]);

  const { buffer, settings } = state;
  const fill = Math.min(100, (buffer.daysCovered / Math.max(1, buffer.targetDays)) * 100);
  const days = settings.activeDays.map((d) => DAY_LABEL[d] || d).join(" & ");
  const winStart = localMinutesOfDay(settings.windowStart);
  const winEnd = localMinutesOfDay(settings.windowEnd);

  return (
    <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
        <div>
          <h3 className="font-bold text-gray-900 text-sm flex items-center gap-2">
            <CalendarClock className="w-4 h-4 text-[#0F3F34]" />
            <span>Udgivelseskalender</span>
          </h3>
          <p className="text-xs text-gray-500 mt-0.5">
            {days} · vindue {settings.windowStart}–{settings.windowEnd} · jitter ±{settings.jitterMinMinutes ?? 30}–
            {settings.jitterMaxMinutes ?? 75} min · {settings.postsPerActiveDay ?? 1} pr. dag
            {settings.skipHolidays ? " · helligdage springes over" : ""}
          </p>
        </div>
        <div className="flex items-center gap-2 text-[11px] font-semibold">
          <span className={`px-2 py-0.5 rounded-md border ${settings.enabled
            ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-gray-200 bg-gray-50 text-gray-600"}`}>
            {settings.enabled ? "Pusher aktiv" : "Pusher slået fra"}
          </span>
          <span className={`px-2 py-0.5 rounded-md border ${state.mode === "live"
            ? "border-[#0F3F34]/30 bg-[#0F3F34]/5 text-[#0F3F34]" : "border-amber-200 bg-amber-50 text-amber-800"}`}>
            {MODE_LABEL[state.mode] || state.mode}
          </span>
        </div>
      </div>

      {/* Buffer gauge */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between text-xs">
          <span className="font-semibold text-gray-700">
            Buffer: {buffer.readyCount} godkendte artikler = {buffer.daysCovered.toLocaleString("da-DK")} dage
          </span>
          <span className={buffer.status === "healthy" ? "text-emerald-700" : "text-amber-700"}>
            Mål {buffer.targetDays} dage{buffer.shortfall > 0 ? ` · mangler ${buffer.shortfall}` : ""}
          </span>
        </div>
        <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
          <div className={`h-full rounded-full ${buffer.status === "healthy" ? "bg-emerald-600" : "bg-amber-500"}`}
               style={{ width: `${fill}%` }} />
        </div>
      </div>

      {/* Day rows */}
      {rows.length === 0 ? (
        <p className="text-xs text-gray-500">
          Ingen slots endnu. Slå pusheren til under Indstillinger, så planlægges de næste 21 dage.
        </p>
      ) : (
        <div className="space-y-1">
          <div className="grid grid-cols-[92px_1fr_minmax(0,260px)] gap-3 text-[10px] text-gray-400 pb-1">
            <span />
            <div className="relative h-3">
              {[8, 10, 12, 14, 16, 18].map((h) => (
                <span key={h} className="absolute -translate-x-1/2" style={{ left: pct(h * 60) }}>{h}:00</span>
              ))}
            </div>
            <span />
          </div>
          {rows.map(([day, slots]) => {
            const holiday = state.holidays.includes(day);
            return (
              <div key={day} className="grid grid-cols-[92px_1fr_minmax(0,260px)] gap-3 items-center py-1.5 border-t border-gray-50">
                <span className="text-xs font-semibold text-gray-700 capitalize">{fmtDay(day)}</span>
                <div className="relative h-5">
                  <div className="absolute inset-y-2 left-0 right-0 bg-gray-50 rounded" />
                  <div className={`absolute inset-y-1.5 rounded ${holiday ? "bg-gray-200" : "bg-emerald-50 border border-emerald-100"}`}
                       style={{ left: pct(winStart), width: `calc(${pct(winEnd)} - ${pct(winStart)})` }} />
                  {slots.map((s) => {
                    const t = localParts(s.plannedAt, tz);
                    return (
                      <span key={s.id} title={`${t.hhmm} · ${SLOT_TEXT[s.status]}${s.title ? ` · ${s.title}` : ""}`}
                            className={`absolute top-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 rounded-full border-2 ${SLOT_STYLE[s.status]}`}
                            style={{ left: pct(t.minutes) }} />
                    );
                  })}
                </div>
                <div className="text-xs text-gray-600 truncate space-y-0.5">
                  {slots.map((s) => (
                    <div key={s.id} className="truncate flex items-center gap-1.5">
                      <span className="font-mono text-gray-900">{localParts(s.plannedAt, tz).hhmm}</span>
                      {s.fastLane && <Zap className="w-3 h-3 text-amber-500 shrink-0" />}
                      <span className="truncate">{s.title || SLOT_TEXT[s.status]}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function localMinutesOfDay(hhmm: string): number {
  const [h, m] = hhmm.split(":").map(Number);
  return (h || 0) * 60 + (m || 0);
}
